import { API } from '../utils/amplifyWrapper';
import { toast } from 'react-toastify';
import classNames from 'classnames';
import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useParams, Link, useHistory } from "react-router-dom";
import * as Sentry from '@sentry/react';
import { CopyToClipboard } from 'react-copy-to-clipboard';

import Spinner from '../components/Spinner';
import EditPodcastSkeleton from '../components/skeletons/pages/EditPodcastSkeleton';
import only from '../utils/Only';
import { useUpdatePodcast, usePodcast } from '../http-hooks/podcast/usePodcast';

import PODCAST_CATEGORIES from '../constants/podcast_categories.json';

import logoIcon from '../assets/logo.svg';
import leftArrowIcon from '../assets/arrow-left.svg';
import copyIcon from '../assets/copy.svg';
import useBeforeUnload from '../hooks/useBeforeUnload';
import ColorPicker from '../components/ColorPicker';
import { Card, PageHeader } from '../components/ui/bootstrap';

function CreatePodcast() {

    const { org_id, id, podcast_id } = useParams();
    const history = useHistory();
    const updatePodcastMutation = useUpdatePodcast({ org_id, integration_id: id, podcast_id });
    
    // Use React Query hook to fetch podcast
    const { data: podcast, isLoading } = usePodcast({ 
        org_id, 
        integration_id: id, 
        podcast_id 
    });
    
    const { register, reset, formState: { errors, isDirty: formIsDirty }, handleSubmit } = useForm();
    const [category, setCategory] = useState(null);
    const [isUpdating, setIsUpdating] = useState(false);
    const [posterFile, setPosterFile] = useState(null);
    const [selectedTab, setSelectedTab] = useState('distribution');

    useBeforeUnload({
        when: formIsDirty,
        message: 'Are you sure you want to leave?',
    })

    useEffect(() => {
        if (podcast) {
            reset(podcast);
            setCategory(podcast.category);
        }
    }, [podcast, reset]);

    const onCategoryChange = (event) => {
        setCategory(event.target.value)
    }

    const onSubmit = async (data) => {

        data = only(data, ['title', 'description', 'author', 'email', 'category', 'sub_category', 'is_explicit', 'is_automatic', 'is_published', 'poster_path'])

        // Convert checkbox values to proper booleans
        data.is_explicit = !!data.is_explicit;
        data.is_automatic = !!data.is_automatic;
        data.is_published = !!data.is_published;

        setIsUpdating(true);

        try {

            if (posterFile !== null) {
                const signedResponse = await API.get(`main`, `/podast/cover/signed-url`, {
                    queryStringParameters: {
                        file_name: posterFile.name,
                        content_type: posterFile.type
                    }
                });

                const requestOptions = {
                    method: 'PUT',
                    body: posterFile,
                    redirect: 'follow'
                };

                const uploadURL = signedResponse.data.signed_url;
                const url = signedResponse.data.url;

                await fetch(uploadURL, requestOptions);

                data.poster_path = url;
            } else {

                data.poster_path = podcast.poster_path;

            }

            // Use React Query mutation - it will handle cache invalidation automatically
            await updatePodcastMutation.mutateAsync(data);
            
            history.push(`/organisation/${org_id}/integration/${id}/podcast`);

        } catch (error) {

            toast.error("Something went wrong while saving the Podcast show settings.")
            Sentry.captureException(error);

        } finally {
            setIsUpdating(false);
        }
    };

    const PodcastPosterImagePicker = () => {
        return (
            <>
                <div className="poster-container d-flex align-items-center justify-content-center mx-auto mx-xl-0 position-relative mb-3">
                    <input type="file" id="poster" onChange={(event) => { setPosterFile(event.target.files[0]) }} />
                    <label htmlFor="poster" className="position-absolute">
                        {
                            (posterFile !== null)
                                ? (
                                    <img className="poster-image" alt="poster" src={URL.createObjectURL(posterFile)} />
                                )
                                : (podcast && podcast.poster_path)
                                    ? (
                                        <img className="poster-image" alt="poster" src={podcast.poster_path} />
                                    )
                                    : (
                                        <img width={64} alt="poster" src={logoIcon} />
                                    )
                        }
                    </label>
                </div>
            </>
        );
    };

    const HostingSettings = function ({ playerSettings, pageSettings }) {

        const { register, getValues, setValue, reset, handleSubmit, watch } = useForm();
        const [logoFile, setLogoFile] = useState(null);
        const [isUpdating, setIsUpdating] = useState(false);

        const watchAll = watch();
    
        useEffect(() => {
    
            reset({
                player_color: playerSettings.player_color,
                player_text_color: playerSettings.text_color,
                player_background_color: playerSettings.background_color,
                page_background_color: pageSettings.background_color,
                download_button: pageSettings.download_button,
                subscribe_button: pageSettings.subscribe_button,
                twitter_share: pageSettings.twitter_share,
                facebook_share: pageSettings.facebook_share,
                remove_branding: pageSettings.remove_branding,
            })
    
        }, [pageSettings, playerSettings]);
    
        const onUpdate = async (data) => {
    
            setIsUpdating(true);
    
            try {
    
                if (logoFile !== null) {
                    const signedResponse = await API.get(`main`, `/podast/logo/signed-url`, {
                        queryStringParameters: {
                            file_name: logoFile.name,
                            content_type: logoFile.type
                        }
                    });
    
                    const requestOptions = {
                        method: 'PUT',
                        body: logoFile,
                        redirect: 'follow'
                    };
    
                    const uploadURL = signedResponse.data.signed_url;
                    const url = signedResponse.data.url;
    
                    await fetch(uploadURL, requestOptions);
    
                    data.logo = url;
                } else {
    
                    data.logo = pageSettings.logo;
    
                }
    
                await API.put('main', `/organisation/${org_id}/integration/${id}/podcast/${podcast_id}/page`, { body: {
                    player_settings: {
                        player_color: data.player_color,
                        text_color: data.player_text_color,
                        background_color: data.player_background_color,
                    },
                    page_settings: {
                        background_color: data.page_background_color,
                        logo: data.logo,
                        facebook_share: data.facebook_share,
                        twitter_share: data.twitter_share,
                        download_button: data.download_button,
                        subscribe_button: data.subscribe_button,
                        remove_branding: data.remove_branding,
                    },
                }, headers: { 'Content-Type': 'application/json' } });
    
                toast.success('Podcast show settings changed successfully!');
                history.push(`/organisation/${org_id}/integration/${id}/podcast`);
    
            } catch (error) {
    
                toast.error("Something went wrong while saving the Podcast show settings.")
                Sentry.captureException(error);
    
            } finally {
                setIsUpdating(false);
            }
    
        }
        
        return (
            <form className="col-xl-8" onSubmit={handleSubmit(onUpdate)}>
                <div className="d-flex flex-column gap-4">
                <Card className="p-4" shadow>
                    <p className="fw-semibold mb-3 text-body-emphasis" style={{ fontSize: '0.75rem', textTransform: 'capitalize', letterSpacing: '0.05em' }}>Page Settings</p>
                    <div className="col-md-8">
                        <div className="fw-semibold mb-2" style={{ fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Logo</div>
                        <div className="logo-container d-flex align-items-center justify-content-center mx-xl-0 position-relative mb-3">
                            <input type="file" id="poster" onChange={(event) => { setLogoFile(event.target.files[0]) }} />
                            <label htmlFor="poster" className="position-absolute">
                                {
                                    (logoFile !== null)
                                        ? (
                                            <img className="logo-image" alt="poster" src={URL.createObjectURL(logoFile)} />
                                        )
                                        : (pageSettings.logo)
                                            ? (
                                                <img className="" alt="poster" width={160} style={{ objectFit: 'cover' }} src={pageSettings.logo} />
                                            )
                                            : (
                                                <img width={64} alt="poster" src={logoIcon} />
                                            )
                                }
                            </label>
                        </div>
                        <div className="mb-3">
                            <label className="form-label">Background Color</label>
                            <div className="input-group mb-3">
                                <span className="input-group-text">
                                    <ColorPicker defaultColor={getValues('page_background_color') ?? pageSettings.background_color} onChange={(color)=>{setValue('page_background_color',color);}}/>
                                </span>
                                <input type="text" className="form-control"  {...register("page_background_color")}/>
                            </div>
                        </div>
                        <div className="form-check my-3 mt-5">
                            <input className="form-check-input" type="checkbox"  {...register("download_button")} />
                            <label className="form-check-label">
                                Download audio button
                            </label>
                        </div>
                        <div className="form-check my-3">
                            <input className="form-check-input" type="checkbox"  {...register("facebook_share")} />
                            <label className="form-check-label">
                                Facebook Sharing
                            </label>
                        </div>
                        <div className="form-check my-3">
                            <input className="form-check-input" type="checkbox"  {...register("twitter_share")} />
                            <label className="form-check-label">
                                Twitter Sharing
                            </label>
                        </div>
                        <div className="form-check my-3">
                            <input className="form-check-input" type="checkbox"  {...register("remove_branding")} />
                            <label className="form-check-label">
                                Remove Listen2It Branding
                            </label>
                        </div>
                        <div className="form-check my-3">
                            <input className="form-check-input" type="checkbox"  {...register("subscribe_button")} />
                            <label className="form-check-label">
                                Subscribe button (RSS Feed)
                            </label>
                        </div>
                    </div>
                </Card>

                <Card className="p-4" shadow>
                    <p className="fw-semibold mb-3 text-body-emphasis" style={{ fontSize: '0.75rem', textTransform: 'capitalize', letterSpacing: '0.05em' }}>Player Settings</p>
                    <div className="col-md-8">
                        <div className="mb-3">
                            <label className="form-label">Player Color</label>
                            <div className="input-group mb-3">
                                <span className="input-group-text">
                                    <ColorPicker defaultColor={getValues('player_color') ?? playerSettings.player_color} onChange={(color)=>{setValue('player_color',color);}}/>
                                </span>
                                <input type="text" className="form-control"  {...register("player_color")}/>
                            </div>
                        </div>
                        <div className="mb-3">
                            <label className="form-label">Player Text Color</label>
                            <div className="input-group mb-3">
                                <span className="input-group-text">
                                    <ColorPicker defaultColor={getValues('player_text_color') ?? playerSettings.text_color} onChange={(color)=>{setValue('player_text_color',color);}}/>
                                </span>
                                <input type="text" className="form-control"  {...register("player_text_color")} />
                            </div>
                        </div>
                        <div className="mb-3"> 
                            <label className="form-label">Player Background Color</label>
                            <div className="input-group mb-3">
                                <span className="input-group-text">
                                    <ColorPicker defaultColor={getValues('player_background_color') ?? playerSettings.background_color} onChange={(color)=>{setValue('player_background_color',color);}}/>
                                </span>
                                <input type="text" className="form-control"  {...register("player_background_color")} />
                            </div>
                        </div>
                    </div>
                </Card>
                </div>
    
                <div className="row my-4">
                    <div className="col-12 text-end">
                        <Link to={`/organisation/${org_id}/integration/${id}/podcast`}><button className="btn btn-outline-primary me-3" type="button">Cancel</button></Link>
                        {
                            (isUpdating) ?
                                (
                                    <button className="btn btn-primary" style={{ width: '145px' }} type="button">
                                        <Spinner color="white" />
                                    </button>
                                )
                                :
                                (
                                    <button className="btn btn-primary" style={{ width: '145px' }} type="submit">Save Changes</button>
                                )
                        }
                    </div>
                </div>
            </form>
        );
    }

    return (
        <>
            <PageHeader
                title="Podcast Settings"
                subtitle="Configure your podcast settings as per your requirements."
                left={
                    <div className="d-flex align-items-center gap-1" style={{ color: 'var(--blue)', cursor: 'pointer', fontSize: 13 }} onClick={() => history.push(`/organisation/${org_id}/integration/${id}/podcast`)}>
                        <img src={leftArrowIcon} width={16} alt="back" />
                        Back
                    </div>
                }
            />
            <ul className="nav nav-tabs mb-4">
                <li class="nav-item">
                    <a onClick={() => setSelectedTab('distribution')} className={classNames("nav-link ", { "active": selectedTab === 'distribution' })} aria-current="page" href="#">General</a>
                </li>
                <li class="nav-item">
                    <a onClick={() => setSelectedTab('hosting')} className={classNames("nav-link ", { "active": selectedTab === 'hosting' })} aria-current="page" href="#">Hosting Page</a>
                </li>
            </ul>
            {
                (selectedTab === 'distribution') && (podcast)
                    && (
                        <form onSubmit={handleSubmit(onSubmit)}>
                            <div className="row flex-xl-row-reverse justify-content-xl-end">
                                {
                                    (isLoading)
                                        ?
                                        (
                                            <EditPodcastSkeleton />
                                        )
                                        :
                                        (
                                            <>
                                                <div className="col-xl-8">
                                                    <div className="d-flex flex-column gap-4">
                                                    <Card className="p-4" shadow>
                                                        <p className="fw-semibold mb-3 text-body-emphasis" style={{ fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Podcast RSS URL</p>
                                                        <div className="mb-3">
                                                            <div className="input-group input-group--search">
                                                                <input type="text" className="form-control border-end-0" value={`${process.env.REACT_APP_PODCAST_RSS_BASE_URL}/${org_id}/${id}/${podcast_id}`} readOnly />
                                                                <span className="input-group-text border-start-0">
                                                                    <CopyToClipboard text={`${process.env.REACT_APP_PODCAST_RSS_BASE_URL}/${org_id}/${id}/${podcast_id}`} onCopy={() => toast.success("Copied to clipboard")}>
                                                                        <img src={copyIcon} style={{ cursor: 'pointer' }} alt="search icon" />
                                                                    </CopyToClipboard>
                                                                </span>
                                                            </div>
                                                        </div>
                                                    </Card>
                                                    <Card className="p-4" shadow>
                                                        <p className="fw-semibold mb-3 text-body-emphasis" style={{ fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>General</p>
                                                        <div className="mb-3">
                                                            <label className="form-label">Podcast Title*</label>
                                                            <input type="text" className={classNames("form-control", { "is-invalid": errors.title })} {...register("title", { required: true, minLength: 3 })} />
                                                            {
                                                                errors.title &&
                                                                <div className="invalid-feedback">
                                                                    {
                                                                        ("required" === errors.title.type) && "Podcast title field is required."
                                                                    }
                                                                    {
                                                                        ("minLength" === errors.title.type) && "Podcast Title field should be at least 3 character long."
                                                                    }
                                                                </div>
                                                            }
                                                        </div>
                                                        <div className="mb-3">
                                                            <label className="form-label">Podcast Description*</label>
                                                            <textarea className="form-control" rows="3" {...register("description", { required: true })} />
                                                        </div>
                                                        <div className="mb-3">
                                                            <label className="form-label">Podcast Author*</label>
                                                            <input type="text" className={classNames("form-control", { "is-invalid": errors.author })} {...register("author", { required: true, minLength: 3 })} />
                                                            {
                                                                errors.author &&
                                                                <div className="invalid-feedback">
                                                                    {
                                                                        ("required" === errors.author.type) && "Author Name field is required."
                                                                    }
                                                                    {
                                                                        ("minLength" === errors.author.type) && "Author Name field should be at least 3 character long."
                                                                    }
                                                                </div>
                                                            }
                                                        </div>
                                                        <div className="mb-3">
                                                            <label className="form-label">Author email*</label>
                                                            <input type="email" className={classNames("form-control", { "is-invalid": errors.author })} {...register("email", { required: true, pattern: /^\S+@\S+\.\S+$/i })} />
                                                            {
                                                                errors.email &&
                                                                <div className="invalid-feedback">
                                                                    {
                                                                        ("required" === errors.email.type) && "Author email field is required."
                                                                    }
                                                                    {
                                                                        ("pattern" === errors.email.type) && "Author email field should be a valid email address"
                                                                    }
                                                                </div>
                                                            }
                                                        </div>
                                                        <div className="mb-3">
                                                            <label className="form-label">Category*</label>
                                                            <select className={classNames("form-select", { "is-invalid": errors.category })}  {...register("category", { required: true })} onChange={onCategoryChange}>
                                                                {
                                                                    Object.keys(PODCAST_CATEGORIES).map((category, i) => {
                                                                        return (
                                                                            <option value={category}>{category}</option>
                                                                        )
                                                                    })
                                                                }
                                                            </select>
                                                            {
                                                                errors.category &&
                                                                <div className="invalid-feedback">
                                                                    {
                                                                        ("required" === errors.category.type) && "Category field is required."
                                                                    }
                                                                </div>
                                                            }
                                                        </div>
                                                        <div className="mb-3">
                                                            <label className="form-label">Sub-Category</label>
                                                            <select className={classNames("form-select", { "is-invalid": errors.sub_category })} {...register("sub_category", {})} disabled={category === null || PODCAST_CATEGORIES[category].length === 0}>
                                                                {
                                                                    category !== null &&
                                                                    PODCAST_CATEGORIES[category].map((sub_category, i) => {
                                                                        return (
                                                                            <option value={sub_category}>{sub_category}</option>
                                                                        )
                                                                    })
                                                                }
                                                            </select>
                                                        </div>
                                                        <div className="mb-3">
                                                            <label className="form-label">Language</label>
                                                            <select className={classNames("form-select")} name="lang_code" disabled value={"English"}>
                                                                <option value="English">English</option>
                                                            </select>
                                                        </div>
                                                        <div className="form-check my-3">
                                                            <input className="form-check-input" type="checkbox" {...register("is_explicit")} onChange={() => { }} />
                                                            <label>
                                                                Contains explicit content
                                                            </label>
                                                        </div>
                                                    </Card>

                                                    <Card className="p-4" shadow>
                                                        <p className="fw-semibold mb-3 text-body-emphasis" style={{ fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Podcast Image*</p>
                                                        {
                                                            errors.is_automatic &&
                                                            <div className="invalid-feedback d-block">
                                                                {
                                                                    ("required" === errors.is_automatic.type) && "Distribution field is required."
                                                                }
                                                            </div>
                                                        }
                                                        <p className="text-muted mb-3" style={{ fontSize: '0.8rem' }}>The image should be square and atleast 1400x1400 pixels in size. We support images in jpg, png, gif and tiff formats up to a maximum of 4MB in size.</p>
                                                        <PodcastPosterImagePicker />
                                                    </Card>

                                                    <Card className="p-4" shadow>
                                                        <p className="fw-semibold mb-3 text-body-emphasis" style={{ fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Status</p>
                                                        <div className="form-check form-switch">
                                                            <input type="checkbox" className="form-check-input" id="is_published"  {...register("is_published")} />
                                                            <label className="form-check-label" htmlFor="is_published">Publish this Podcast?</label>
                                                        </div>
                                                    </Card>

                                                    <Card className="p-4" shadow>
                                                        <p className="fw-semibold mb-3 text-body-emphasis" style={{ fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Automatic Publishing</p>
                                                        {
                                                            errors.is_automatic &&
                                                            <div className="invalid-feedback d-block">
                                                                {
                                                                    ("required" === errors.is_automatic.type) && "Distribution field is required."
                                                                }
                                                            </div>
                                                        }
                                                        <p className="text-muted mb-3" style={{ fontSize: '0.8rem' }}>All your audios will be automatically added (as soon as they are created) as podcast episodes and published if this is enabled. You can still remove them later if you want to.</p>
                                                        <div className="form-check form-switch">
                                                            <input disabled={true} type="checkbox" className="form-check-input" id="is_automatic"  {...register("is_automatic")} />
                                                            <label className="form-check-label" htmlFor="is_automatic">Automatically publish all my audios as episodes in this show</label>
                                                        </div>
                                                    </Card>
                                                    </div>
                                                    
                                                    <div className="row my-4">
                                                        <div className="col-12 text-end">
                                                            <Link to={`/organisation/${org_id}/integration/${id}/podcast`}><button className="btn btn-outline-primary me-3" type="button">Cancel</button></Link>
                                                            {
                                                                (isUpdating) ?
                                                                    (
                                                                        <button className="btn btn-primary" style={{ width: '145px' }} type="button">
                                                                            <Spinner color="white" />
                                                                        </button>
                                                                    )
                                                                    :
                                                                    (
                                                                        <button className="btn btn-primary" style={{ width: '145px' }} type="submit">Save Changes</button>
                                                                    )
                                                            }
                                                        </div>
                                                    </div>
                                                </div>
                                            </>
                                        )
                                }
                            </div>
                        </form>
                    )
            }
            {
                ('hosting' === selectedTab) && (podcast)
                    && (
                        <HostingSettings playerSettings={podcast.player_settings} pageSettings={podcast.page_settings} />
                    )
            }
        </>
    );
}

export default CreatePodcast;
